/**
 * MessageViewer — the copy a recipient actually received, as it was sent.
 *
 * The run record keeps the subject and the SES message ids; it does not keep the
 * message, and SES keeps nothing once a send is accepted. So this reads the archive the
 * dispatcher wrote at send time (CONTRACT-MAIL §1). It deliberately does NOT re-render
 * the template: the figures, the wording and the link expiry have all moved on since,
 * and "what does this template produce today" is a different question from "what does
 * the client have in their inbox".
 *
 * Three rules this component exists to hold:
 *
 *  1. **The frame is sandboxed with no scripts and no same-origin** (§5). The body is a
 *     full HTML document rendered inside an authenticated console; `allow-scripts` and
 *     `allow-same-origin` together would let it reach back into the console's origin and
 *     read an operator's session. Links are allowed to open in a new tab and nothing
 *     else is. `referrerPolicy="no-referrer"` keeps the console URL out of any click.
 *  2. **Both parts are offered.** A recipient on a gateway that strips HTML read the
 *     text part, and that is the copy worth inspecting when they say the mail looked
 *     wrong — so the plain-text toggle is a first-class control, not a debug affordance.
 *  3. **An operator download is its own audit event** (§4). It mints a fresh grant
 *     attributed to `console:{operator}` and never reuses or reveals the recipient's
 *     token — the console holds only token hashes by design, and conflating the two
 *     corrupts the trail in nrd-access.
 *
 * The archived links are live-or-dead exactly as the recipient's are: they carry the
 * original tokens and obey the original 7-day window, so clicking one inside the frame
 * after a week gets the same 410 the client gets. That is correct, and is said in words
 * under the frame rather than left to be discovered.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import Badge from '@/components/Badge';
import Button, { ButtonGroup } from '@/components/Button';
import EmptyState from '@/components/EmptyState';
import Table, { type Column } from '@/components/Table';
import { useToast } from '@/components/Toast';
import { copyText } from '@/components/GrantList';
import { startDownload } from '@/lib/download';
import { api, errorMessage } from '@/lib/api';
import {
  formatBytes,
  formatCount,
  istDateTime,
  istDateTimeExact,
  timeAgo,
  toDate,
} from '@/lib/format';
import { MESSAGE_FAILED_STATUSES } from '@/lib/types';
import type {
  Message,
  MessageArtifact,
  MessageDetail,
  MessagePart,
  MessageStatus,
  SesEvent,
} from '@/lib/types';

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

interface MessageStatusSpec {
  label: string;
  tone: 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'accent' | 'muted';
  glyph: string;
  /** Dashed border: the status is still expected to move. */
  pending?: boolean;
  /** Double border: terminal, and bad. */
  terminalBad?: boolean;
  title: string;
}

/**
 * Status moves forward only — `sent` → `delivered` → `opened` — and the three failure
 * states are terminal (CONTRACT-MAIL §3). The badge encodes that in form as well as
 * colour: dashed while a status is still expected to change, double-ruled once it can
 * never improve. A bounce has to be findable in a 200-row list on a greyscale screen.
 */
const MESSAGE_STATUS_SPEC: Record<MessageStatus, MessageStatusSpec> = {
  sent: {
    label: 'Sent',
    tone: 'info',
    glyph: '↑',
    pending: true,
    title: 'Accepted by SES. Delivery to the recipient is not yet confirmed.',
  },
  delivered: {
    label: 'Delivered',
    tone: 'ok',
    glyph: '✓',
    title: "Confirmed delivered to the recipient's mail server.",
  },
  opened: {
    label: 'Opened',
    tone: 'ok',
    glyph: '◉',
    title: 'Delivered, and opened at least once.',
  },
  bounced: {
    label: 'Bounced',
    tone: 'danger',
    glyph: '⊘',
    terminalBad: true,
    title: 'Never reached the recipient. Terminal — a later delivery cannot overwrite it.',
  },
  complained: {
    label: 'Complained',
    tone: 'danger',
    glyph: '⚑',
    terminalBad: true,
    title: 'The recipient marked it as spam. Terminal.',
  },
  rejected: {
    label: 'Rejected',
    tone: 'danger',
    glyph: '✕',
    terminalBad: true,
    title: 'SES refused to send it. Nothing left our side.',
  },
};

/**
 * The human label for a status value. The stored value and the query-string value are
 * unchanged — this is only what a person reads, so the filter dropdown stops offering a
 * vocabulary (`bounced`) that matches nothing on the screen it filters (`Bounced`).
 */
export function messageStatusLabel(status: MessageStatus): string {
  return MESSAGE_STATUS_SPEC[status]?.label ?? status;
}

/** The one mapping for message status; the list and the viewer both use it. */
export function MessageStatusBadge({
  status,
  className,
}: {
  status: MessageStatus;
  className?: string;
}) {
  const spec = MESSAGE_STATUS_SPEC[status] ?? {
    label: status,
    tone: 'neutral' as const,
    glyph: '·',
    title: status,
  };
  return (
    <Badge
      tone={spec.tone}
      glyph={spec.glyph}
      pending={spec.pending}
      terminalBad={spec.terminalBad}
      title={spec.title}
      className={className}
    >
      {spec.label}
    </Badge>
  );
}

/** True when this status can never improve — the list draws these rows differently. */
export function isFailedStatus(status: MessageStatus): boolean {
  return (MESSAGE_FAILED_STATUSES as readonly string[]).includes(status);
}

/** `to` / `cc` / `bcc`, so a surprised "why did I get this?" is answerable at a glance. */
export function RecipientKindBadge({ kind }: { kind: Message['recipient_kind'] }) {
  return (
    <Badge
      tone={kind === 'bcc' ? 'muted' : 'neutral'}
      glyph={kind === 'to' ? '→' : kind === 'cc' ? '⧉' : '◌'}
      title={
        kind === 'to'
          ? 'Addressed directly'
          : kind === 'cc'
            ? 'Copied — visible to the other recipients'
            : 'Blind copy — not visible to the other recipients'
      }
    >
      {kind}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Timeline
// ---------------------------------------------------------------------------

type EventTone = 'default' | 'ok' | 'warn' | 'danger' | 'info' | 'muted';

const TONE_INK: Record<EventTone, string> = {
  default: 'var(--text-2)',
  ok: 'var(--ok)',
  warn: 'var(--warn)',
  danger: 'var(--danger)',
  info: 'var(--info)',
  muted: 'var(--text-3)',
};

const TONE_FILL: Record<EventTone, string> = {
  default: 'var(--surface-2)',
  ok: 'var(--ok-soft)',
  warn: 'var(--warn-soft)',
  danger: 'var(--danger-soft)',
  info: 'var(--info-soft)',
  muted: 'var(--muted-soft)',
};

const TONE_LINE: Record<EventTone, string> = {
  default: 'var(--line)',
  ok: 'var(--ok-line)',
  warn: 'var(--warn-line)',
  danger: 'var(--danger-line)',
  info: 'var(--info-line)',
  muted: 'var(--muted-line)',
};

const SES_SPEC: Record<string, { title: string; tone: EventTone; glyph: string }> = {
  Send: { title: 'Handed to SES', tone: 'info', glyph: '↑' },
  Delivery: { title: 'Delivered', tone: 'ok', glyph: '✓' },
  Bounce: { title: 'Bounced', tone: 'danger', glyph: '⊘' },
  Complaint: { title: 'Marked as spam', tone: 'danger', glyph: '⚑' },
  Reject: { title: 'Rejected by SES', tone: 'danger', glyph: '✕' },
  Open: { title: 'Opened', tone: 'muted', glyph: '◉' },
  Click: { title: 'Link clicked in the email', tone: 'info', glyph: '↗' },
  DeliveryDelay: { title: 'Delivery delayed', tone: 'warn', glyph: '◔' },
  RenderingFailure: { title: 'Template failed to render', tone: 'danger', glyph: '✕' },
};

/**
 * One spec, whichever spelling arrives.
 *
 * Two vocabularies reach this component for the same events. `nrd-ses-events` rows come
 * through the API in SES's own spelling (`DeliveryDelay`), and `nrd-messages.last_event`
 * carries the events handler's normalised code (`delivery_delay`). Looking up only one of
 * them is how a bounced message's latest event renders as the raw string `bounce`, with no
 * badge and no severity — which is precisely the row an operator opened the viewer to read.
 */
function sesSpec(eventType: string): { title: string; tone: EventTone; glyph: string } {
  const camel = eventType
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
  return (
    SES_SPEC[eventType] ??
    SES_SPEC[camel] ?? { title: eventType, tone: 'default' as EventTone, glyph: '·' }
  );
}

interface ViewerEvent {
  id: string;
  sortKey: number;
  at: Date;
  title: string;
  detail?: ReactNode;
  tone: EventTone;
  glyph: string;
  /**
   * Every occurrence folded into this row, oldest first, when a run of identical
   * engagement events was collapsed (§M15). Length 1 means nothing was folded.
   */
  occurrences?: Date[];
}

/**
 * Events that repeat and say the same thing each time. A client who opens a report
 * twelve times produced twelve ~70px rows saying "Opened", which buried the delivery
 * story the timeline exists to tell. Send / Delivery / Bounce / Complaint / Reject are
 * state transitions and are never folded — those are the ones whose ordering matters.
 */
const FOLDABLE_TITLES = new Set([sesSpec('Open').title, sesSpec('Click').title]);

function foldRepeats(events: ViewerEvent[]): ViewerEvent[] {
  const out: ViewerEvent[] = [];
  for (const event of events) {
    const previous = out[out.length - 1];
    if (previous && FOLDABLE_TITLES.has(event.title) && previous.title === event.title) {
      previous.occurrences = [...(previous.occurrences ?? [previous.at]), event.at];
      continue;
    }
    out.push({ ...event, occurrences: FOLDABLE_TITLES.has(event.title) ? [event.at] : undefined });
  }
  return out;
}

/**
 * Delivery history for ONE message.
 *
 * The rows come from nrd-ses-events for this message id. Nothing here is interpolated:
 * an event with no stored time is not listed at all rather than given a plausible one —
 * a fabricated time in an audit surface is worse than a missing one. When the event rows
 * cannot be read, the single `last_event` the message row holds stands in, and is
 * labelled as the latest event rather than dressed up as a full history.
 */
export function buildMessageTimeline(message: Message, rows: SesEvent[] = []): ViewerEvent[] {
  const events: ViewerEvent[] = [];

  const push = (
    id: string,
    at: Date | null,
    title: string,
    tone: EventTone,
    glyph: string,
    detail?: ReactNode,
  ): void => {
    if (!at) return;
    events.push({ id, at, sortKey: at.getTime(), title, tone, glyph, detail });
  };

  // `sent_at` and a `Send` event are the same moment recorded twice. When the event
  // rows are here, they are the record; the message row's own timestamp only stands in
  // for a send that SES never reported back. Both spellings are checked because both
  // vocabularies reach this component — see sesSpec().
  if (!rows.some((event) => event.event_type === 'Send' || event.event_code === 'send')) {
    push(
      'sent',
      toDate(message.sent_at),
      'Handed to SES',
      'info',
      '↑',
      <>
        {message.recipient_email} ({message.recipient_kind})
      </>,
    );
  }

  for (const event of rows) {
    const spec = sesSpec(event.event_type);
    push(
      // The row's own sort key: unique per recipient within one event, which the
      // timestamp is not.
      `ses-${event.event_key ?? `${event.event_at}-${event.event_type}`}`,
      toDate(event.event_at),
      spec.title,
      spec.tone,
      spec.glyph,
      event.detail ? (
        // In FULL, wrapping, and not dim. On a Bounce this is the SMTP diagnostic — the
        // reason the mail failed — and the reason sits at the END of the string. It was
        // cut at 140 characters with no title, which made the tail unrecoverable by any
        // means. A failure diagnostic is body text, not a footnote.
        <span className="small" style={{ overflowWrap: 'anywhere' }}>
          {event.detail}
        </span>
      ) : event.event_type === 'Send' ? (
        <>
          {event.recipient} ({message.recipient_kind})
        </>
      ) : undefined,
    );
  }

  if (rows.length === 0 && message.last_event && message.last_event_at) {
    const spec = sesSpec(message.last_event);
    push(
      'last-event',
      toDate(message.last_event_at),
      spec.title,
      spec.tone,
      spec.glyph,
      <span className="dim">
        Latest event recorded against this message. The full event history lives on the run.
      </span>,
    );
  }

  return foldRepeats(
    events
      .map((event, index) => ({ event, index }))
      .sort((a, b) => a.event.sortKey - b.event.sortKey || a.index - b.index)
      .map((entry) => entry.event),
  );
}

function Timeline({ events, openCount }: { events: ViewerEvent[]; openCount: number }) {
  if (events.length === 0) {
    return (
      <div className="small dim">
        Nothing recorded against this message yet. Delivery, bounce and open events appear here
        as SES reports them.
      </div>
    );
  }

  return (
    <div className="stack">
      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {events.map((event, index) => {
          const last = index === events.length - 1;
          return (
            <li
              key={event.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '20px minmax(0, 1fr)',
                columnGap: 'var(--s-3)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minHeight: '100%',
                }}
              >
                <span
                  aria-hidden="true"
                  className="mono"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    fontSize: 'var(--fs-xs)',
                    flex: '0 0 auto',
                    border: `1px solid ${TONE_LINE[event.tone]}`,
                    background: TONE_FILL[event.tone],
                    color: TONE_INK[event.tone],
                  }}
                >
                  {event.glyph}
                </span>
                {last ? null : (
                  <span
                    aria-hidden="true"
                    style={{ flex: 1, width: 1, background: 'var(--line)', marginTop: 2 }}
                  />
                )}
              </div>

              <div
                className="stack-tight"
                style={{ paddingBottom: last ? 0 : 'var(--s-3)', minWidth: 0 }}
              >
                <span className="strong" style={{ color: TONE_INK[event.tone] }}>
                  {event.title}
                  {(event.occurrences?.length ?? 1) > 1 ? (
                    <span className="dim"> · {formatCount(event.occurrences!.length)} times</span>
                  ) : null}
                </span>
                {(event.occurrences?.length ?? 1) > 1 ? (
                  <span className="small dim">
                    first {istDateTimeExact(event.occurrences![0])} · last{' '}
                    {istDateTimeExact(event.occurrences![event.occurrences!.length - 1])}
                  </span>
                ) : (
                  <span className="small dim">
                    {istDateTimeExact(event.at)} · {timeAgo(event.at)}
                  </span>
                )}
                {(event.occurrences?.length ?? 1) > 1 ? (
                  <details>
                    <summary className="xs dim" style={{ cursor: 'pointer' }}>
                      Every time (IST)
                    </summary>
                    <div className="stack-tight" style={{ marginTop: 'var(--s-1)' }}>
                      {event.occurrences!.map((at, n) => (
                        <span key={`${at.getTime()}-${n}`} className="xs dim num">
                          {istDateTimeExact(at)}
                        </span>
                      ))}
                    </div>
                  </details>
                ) : null}
                {event.detail ? <span className="small">{event.detail}</span> : null}
              </div>
            </li>
          );
        })}
      </ol>

      {openCount > 0 ? (
        <div className="small dim">
          Opened {formatCount(openCount)} {openCount === 1 ? 'time' : 'times'}. Open tracking is a
          pixel fetch — a client whose mail app blocks images reads the message without ever
          reporting one, so a count of zero is not proof it went unread.
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Viewer
// ---------------------------------------------------------------------------

export interface MessageViewerProps {
  messageId: string;
  /**
   * The list row, if the caller already has it. Renders the header immediately while
   * the body is still being read from S3, instead of showing an empty dialog.
   */
  summary?: Message | null;
  /**
   * Height of the body frame when it is not expanded. CSS length, not a number: the
   * default fills the room an xl dialog actually has instead of a fixed 520px letterbox
   * on a 1080p screen.
   */
  frameHeight?: string;
}

export function MessageViewer({
  messageId,
  summary = null,
  frameHeight = 'min(72vh, 720px)',
}: MessageViewerProps) {
  const toast = useToast();

  const [detail, setDetail] = useState<MessageDetail | null>(null);
  const [events, setEvents] = useState<SesEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [part, setPart] = useState<MessagePart>('html');
  const [expanded, setExpanded] = useState(false);
  const [minting, setMinting] = useState<string | null>(null);

  useEffect(() => {
    if (!messageId) return;
    let cancelled = false;
    setLoading(true);

    api.messages
      .get(messageId)
      .then((result) => {
        if (cancelled) return;
        setDetail(result);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(errorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [messageId, reloadKey]);

  const head: Message | MessageDetail | null = detail ?? summary;

  // The body is its own object and can be null with a reason — a sent message whose
  // archived copy has gone answers 200, not an error (CONTRACT-MAIL §4).
  const body = detail?.body ?? null;
  const hasHtml = typeof body?.html === 'string' && body.html.length > 0;
  const hasText = typeof body?.text === 'string' && body.text.length > 0;

  // Land on whichever part actually exists. A toggle that opens on an empty pane reads
  // as a broken viewer rather than as a message that was archived text-only.
  useEffect(() => {
    if (!detail) return;
    setPart(hasHtml ? 'html' : hasText ? 'text' : 'html');
  }, [detail, hasHtml, hasText]);

  /**
   * The delivery history for THIS copy. nrd-ses-events is keyed by message id but is
   * read per run, so the run's events are fetched and filtered down. It is supporting
   * detail: a failure here costs the timeline its rows, never the screen its message,
   * and the row's own `last_event` stands in.
   */
  useEffect(() => {
    const runId = detail?.run_id;
    if (!runId) return;
    let cancelled = false;
    api.runs
      .sesEvents(runId, { limit: 200 })
      .then((result) => {
        if (cancelled) return;
        setEvents(result.items.filter((event) => event.message_id === messageId));
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      });
    return () => {
      cancelled = true;
    };
  }, [detail?.run_id, messageId]);

  const timeline = useMemo(
    () => (head ? buildMessageTimeline(head, events) : []),
    [head, events],
  );

  const download = useCallback(
    async (artifact: MessageArtifact) => {
      setMinting(artifact.kind);
      try {
        const minted = await api.messages.download(messageId, { kind: artifact.kind });
        const filename = minted.filename ?? artifact.filename;
        startDownload(minted.url, filename);
        toast.success('Downloading', {
          duration: 12_000,
          description: `${filename}${
            minted.expires_at ? ` · the link stays valid until ${istDateTime(minted.expires_at)}` : ''
          }`,
          action: {
            label: 'Copy link',
            onClick: () => {
              void copyText(minted.url).then((ok) =>
                ok
                  ? toast.success('Link copied')
                  : toast.warn('Could not reach the clipboard in this browser'),
              );
            },
          },
        });
      } catch (err) {
        toast.error(errorMessage(err));
      } finally {
        setMinting(null);
      }
    },
    [messageId, toast],
  );

  const artifactColumns: Column<MessageArtifact>[] = [
    {
      // `kind` and `filename` say the same thing on nearly every row, so the kind is the
      // filename's tooltip and the 130px goes to the name an operator actually reads.
      key: 'filename',
      header: 'File',
      mono: true,
      render: (a) => (
        <div className="truncate" title={`${a.filename} · ${a.kind}`}>
          {a.filename}
        </div>
      ),
    },
    {
      key: 'rows',
      header: 'Rows',
      align: 'right',
      mono: true,
      width: '92px',
      render: (a) => formatCount(a.rows),
    },
    {
      key: 'bytes',
      header: 'Size',
      align: 'right',
      mono: true,
      width: '92px',
      render: (a) => formatBytes(a.bytes),
    },
    {
      key: 'actions',
      header: '',
      width: '124px',
      align: 'right',
      nowrap: true,
      render: (a) => (
        <Button
          size="sm"
          loading={minting === a.kind}
          disabled={minting !== null && minting !== a.kind}
          onClick={() => void download(a)}
          title="Mints a fresh operator grant — the recipient's own token is never reused"
        >
          Download
        </Button>
      ),
    },
  ];

  if (!head && loading) {
    return (
      <div className="cluster">
        <span className="spinner" aria-hidden="true" />
        <span className="muted">Reading the archived copy…</span>
      </div>
    );
  }

  if (!head) {
    return (
      <EmptyState
        tone="danger"
        title="Could not load this message"
        description={error ?? 'No archived message with that id.'}
        action={
          <Button variant="primary" onClick={() => setReloadKey((key) => key + 1)}>
            Retry
          </Button>
        }
      />
    );
  }

  const failed = isFailedStatus(head.status);
  // An API that omits an empty list rather than sending [] must not blank the dialog.
  const artifacts = head.artifacts ?? [];

  return (
    <div className="stack">
      {error && detail ? (
        <div className="callout tone-warn" role="alert">
          <span className="callout-glyph" aria-hidden="true">
            ▲
          </span>
          <span>{error} Showing the last copy that loaded.</span>
        </div>
      ) : null}

      {failed ? (
        <div className="callout tone-danger" role="alert">
          <span className="callout-glyph" aria-hidden="true">
            ⊘
          </span>
          <div className="stack-tight">
            <div className="strong">
              This copy never reached {head.recipient_email}
              {head.status === 'bounced' ? ' — it bounced.' : `. Status: ${head.status}.`}
            </div>
            <div>
              The status is terminal: a later delivery event cannot overwrite it. What is shown
              below is what we handed to SES, not what anybody read.
            </div>
          </div>
        </div>
      ) : null}

      {/* One dense line, not a seven-row definition list. Sent, To and Subject are all
          in the dialog header already; the exact send time is the first row of the
          Delivery timeline below, at seconds precision. The archived object keys move to
          the foot of the dialog — they are audit detail, not a reading aid, and they used
          to sit between the operator and the message they opened. */}
      <div className="cluster-tight small">
        <MessageStatusBadge status={head.status} />
        {head.open_count > 0 ? (
          <span className="dim">
            opened {formatCount(head.open_count)} {head.open_count === 1 ? 'time' : 'times'}
          </span>
        ) : null}
        <span className="dim">·</span>
        <Link to={`/clients/${head.client_id}`}>{head.client_name ?? head.client_id}</Link>
        {head.client_name ? <span className="dim mono xs">{head.client_id}</span> : null}
        <span className="dim">·</span>
        <Link className="mono" to={`/runs/${head.run_id}`} title="The links, grants and access log for this dispatch">
          {head.run_id}
        </Link>
      </div>

      <div className="toolbar">
        <ButtonGroup label="Message part">
          <Button
            size="sm"
            className={part === 'html' ? 'is-selected' : ''}
            aria-pressed={part === 'html'}
            disabled={!hasHtml}
            title={hasHtml ? 'The HTML part, exactly as sent' : 'No HTML part was archived'}
            onClick={() => setPart('html')}
          >
            HTML
          </Button>
          <Button
            size="sm"
            className={part === 'text' ? 'is-selected' : ''}
            aria-pressed={part === 'text'}
            disabled={!hasText}
            title={
              hasText
                ? 'The plain-text part — what a gateway that strips HTML delivered'
                : 'No text part was archived'
            }
            onClick={() => setPart('text')}
          >
            Plain text
          </Button>
        </ButtonGroup>

        <span className="grow" />

        {loading ? (
          <span className="cluster-tight small dim">
            <span className="spinner" aria-hidden="true" />
            <span>Reading the archived copy…</span>
          </span>
        ) : null}

        <Button size="sm" onClick={() => setExpanded((value) => !value)}>
          {expanded ? 'Shrink' : 'Expand'}
        </Button>
      </div>

      {detail?.body_missing ? (
        <div className="callout tone-warn" role="alert">
          <span className="callout-glyph" aria-hidden="true">
            ◈
          </span>
          <div className="stack-tight">
            <div className="strong">
              {detail.body_missing === 'not_archived'
                ? 'This copy was never archived.'
                : body
                  ? 'One part of this copy could not be read.'
                  : 'The archived copy is gone.'}
            </div>
            <div>
              {detail.body_missing_detail ||
                'The message row is intact; only the archived copy cannot be read.'}{' '}
              It is not re-rendered from the template, because that would show a different
              message from the one that was sent.
            </div>
          </div>
        </div>
      ) : null}

      {body?.truncated ? (
        <div className="callout tone-warn">
          <span className="callout-glyph" aria-hidden="true">
            ▲
          </span>
          <span>
            This copy is larger than the viewer reads, so what is shown below is the start of
            it. The archived object itself is complete.
          </span>
        </div>
      ) : null}

      {part === 'html' && hasHtml ? (
        <div className="stack-tight">
          {/*
            No allow-scripts and no allow-same-origin (CONTRACT-MAIL §5). Those two
            together would let the frame reach the console's origin. Popups are allowed
            so a link in the mail opens in a new tab and stays outside the sandbox.
            The white ground is the mail client's, not the console's — a dark console
            theme must not repaint a message that was sent on white.
          */}
          <iframe
            title="Archived message, exactly as sent"
            sandbox="allow-popups allow-popups-to-escape-sandbox"
            srcDoc={body?.html ?? ''}
            referrerPolicy="no-referrer"
            loading="lazy"
            style={{
              width: '100%',
              height: expanded ? '78vh' : frameHeight,
              border: '1px solid var(--line)',
              borderRadius: 'var(--r-2)',
              background: '#FFFFFF',
              colorScheme: 'light',
              display: 'block',
            }}
          />
          <span className="xs dim">
            Rendered in a sandboxed frame: no scripts, no access to this console, links open in a
            new tab. The download links inside carry the recipient's original tokens and the
            original 7-day window — after that they return a 410 here exactly as they do in the
            client's inbox.
          </span>
        </div>
      ) : null}

      {part === 'text' && hasText ? (
        <div className="stack-tight">
          <pre
            className="code-block"
            style={{
              margin: 0,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              maxHeight: expanded ? '78vh' : frameHeight,
              overflowY: 'auto',
              color: 'var(--text)',
            }}
          >
            {body?.text}
          </pre>
          <span className="xs dim">
            The plain-text part, as sent. Long lines are wrapped for reading here; the archived
            copy is unchanged.
          </span>
        </div>
      ) : null}

      {!loading && !detail?.body_missing && ((part === 'html' && !hasHtml) || (part === 'text' && !hasText)) ? (
        <EmptyState
          title={part === 'html' ? 'No HTML part archived' : 'No plain-text part archived'}
          description={
            part === 'html'
              ? 'This message was archived without an HTML body. The plain-text part is the copy that was sent.'
              : 'This message was archived without a text body. The HTML part is the copy that was sent.'
          }
          action={
            <Button
              variant="primary"
              disabled={part === 'html' ? !hasText : !hasHtml}
              onClick={() => setPart(part === 'html' ? 'text' : 'html')}
            >
              {part === 'html' ? 'Show plain text' : 'Show HTML'}
            </Button>
          }
        />
      ) : null}

      {artifacts.length > 0 ? (
        <div className="stack-tight">
          <div className="strong small">What this mail offered</div>
          <Table
            columns={artifactColumns}
            rows={artifacts}
            rowKey={(artifact, index) => `${artifact.kind}-${artifact.filename}-${index}`}
            dense
          />
          <span className="xs dim">
            Downloading mints a new operator grant against your own name. It does not reuse or
            reveal the recipient's token, so opening a file here is recorded as your access, not
            as the client's.
          </span>
        </div>
      ) : (
        <div className="small dim">This mail carried no artifacts.</div>
      )}

      <div className="stack-tight">
        <div className="strong small">Delivery</div>
        <Timeline events={timeline} openCount={head.open_count} />
      </div>

      <details>
        <summary className="xs dim" style={{ cursor: 'pointer' }}>
          Archived objects
        </summary>
        <div className="stack-tight" style={{ marginTop: 'var(--s-2)' }}>
          <span className="mono small" style={{ overflowWrap: 'anywhere' }}>
            {detail?.s3_html ?? head.s3_html ?? '—'}
          </span>
          <span className="mono small" style={{ overflowWrap: 'anywhere' }}>
            {detail?.s3_text ?? head.s3_text ?? '—'}
          </span>
          <span className="xs dim">
            Written at send time to nrd-artifacts-442228338612 and kept for 400 days — longer
            than the links it contains.
          </span>
        </div>
      </details>
    </div>
  );
}

export default MessageViewer;
